import type {
  AlertRule,
  ApplicationWithStats,
  LogWithRelations,
  MessageTypeWithStats,
  OverviewMetrics,
} from '@owl/shared';
import { OVERVIEW_PINNED_APPS_MAX } from '@owl/shared';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { Range, ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { DonutChart } from '../components/charts/DonutChart';
import { SparkLine } from '../components/charts/SparkLine';
import {
  StackedTimeline,
  type TimelineSelection,
  readHiddenTimelineTypes,
} from '../components/charts/StackedTimeline';
import { Icon } from '../components/icons';
import { AppHealthDot, Kpi, MessageTypeChip } from '../components/shared';
import { activateOnKey } from '../lib/activateOnKey';
import { api } from '../lib/api';
import { bucketEndIso, formatBucketRange } from '../lib/buckets';
import { greeting } from '../lib/greeting';
import { formatDuration, formatTime, rangeIso, timeAgo } from '../lib/time';
import { useEscapeKey } from '../lib/useEscapeKey';

export function Overview({ ctx }: { ctx: ShellCtx }) {
  const [data, setData] = useState<OverviewMetrics | null>(null);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [mts, setMts] = useState<MessageTypeWithStats[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [logsModalMtId, setLogsModalMtId] = useState<string | null>(null);
  // Bucket clicked on the timeline; opens the same logs modal scoped to that time window.
  const [bucketSel, setBucketSel] = useState<TimelineSelection | null>(null);
  // Message types the user has hidden in the timeline legend. Mirrors StackedTimeline
  // and is also used to filter the distribution donut so both widgets stay coherent.
  const [hiddenMtNames, setHiddenMtNames] = useState<Set<string>>(() => readHiddenTimelineTypes());
  const [pinMenuOpen, setPinMenuOpen] = useState(false);
  const pinMenuRef = useRef<HTMLDivElement>(null);
  // Apps the whole Overview is scoped to (empty = every app in the environment).
  // Kept per environment because DEV and PROD have different app sets.
  const [appFilterIds, setAppFilterIds] = useState<string[]>([]);
  const [appFilterOpen, setAppFilterOpen] = useState(false);
  const appFilterRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  // Recomputed on every refresh: rangeIso stamps `to` with the current time, and keyed
  // on the range alone the window froze when the view mounted.
  const range = useMemo(() => {
    void ctx.refreshTick;
    return rangeIso(ctx.range);
  }, [ctx.range, ctx.refreshTick]);
  // For MTD, the chart x-axis spans the full current month (day 1 → last day at 23:59:59.999
  // local time) so days that haven't happened yet appear as empty slots. KPIs and the API query
  // keep using `range.to = now` so they only reflect what has actually happened so far.
  const chartTo = useMemo(() => {
    if (ctx.range !== 'mtd') return range.to;
    const now = new Date();
    const endLocal = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return endLocal.toISOString();
  }, [ctx.range, range.to]);

  // Selected apps belong to one environment; switching env clears the filter.
  const environment = ctx.environment;
  useEffect(() => {
    void environment;
    setAppFilterIds([]);
  }, [environment]);

  const appFilterKey = appFilterIds.join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: appFilterIds is tracked via appFilterKey
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, a, t, r] = await Promise.all([
          api.overview(
            range.from,
            range.to,
            ctx.environment,
            appFilterIds.length > 0 ? appFilterIds : undefined,
          ),
          api.listApplications(ctx.environment),
          api.listMessageTypes(),
          api.listAlertRules(),
        ]);
        if (cancelled) return;
        setData(m);
        setApps(a.items);
        setMts(t.items);
        setRules(r.items);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, ctx.refreshTick, ctx.environment, appFilterKey]);

  const mtById = useMemo(() => new Map(mts.map((m) => [m.id, m])), [mts]);
  const appById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const ruleById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);

  const sparkBuckets = useMemo(() => {
    if (!data) return [];
    return data.timeline.map((b) => Object.values(b.counts).reduce((a, c) => a + c, 0));
  }, [data]);

  const activeApps = apps.filter((a) => a.status === 'active').length;
  const pausedApps = apps.filter((a) => a.status === 'paused').length;

  const criticalAlertsCount = useMemo(() => {
    if (!data) return 0;
    let c = 0;
    for (const ev of data.recentAlerts) {
      const r = ruleById.get(ev.ruleId);
      if (r && r.severity === 'critical') c += 1;
    }
    return c;
  }, [data, ruleById]);

  const healthMtIds = useMemo(() => {
    const byName = new Map<string, string>();
    for (const t of mts) byName.set(t.name, t.id);
    const pick = (names: readonly string[]) =>
      names.map((n) => byName.get(n)).filter((x): x is string => Boolean(x));
    return {
      success: pick(['SUCCESS']),
      warning: pick(['WARN']),
      error: pick(['ERROR']),
      neutral: pick(['INFO', 'DEBUG', 'TRACE']),
    };
  }, [mts]);

  const errorRateDelta = useMemo(() => {
    if (!data) return '—';
    const prev = data.totals.previousErrorRate;
    if (prev === null) return `rango ${ctx.range === 'mtd' ? 'MTD' : ctx.range}`;
    const diffPp = (data.totals.errorRate - prev) * 100;
    if (Math.abs(diffPp) < 0.005) return 'sin cambio vs periodo prev';
    const arrow = diffPp > 0 ? '↑' : '↓';
    return `${arrow} ${Math.abs(diffPp).toFixed(2)}pp vs periodo prev`;
  }, [data, ctx.range]);

  const distributionData = useMemo(() => {
    if (!data) return [];
    // Mirror the timeline legend: any type the user hid there is also hidden here.
    return data.distribution
      .map((d) => {
        const mt = mtById.get(d.messageTypeId);
        return {
          messageTypeId: d.messageTypeId,
          value: d.count,
          color: mt?.color ?? '#888',
          label: mt?.name ?? d.messageTypeId.slice(0, 6),
        };
      })
      .filter((d) => d.value > 0 && !hiddenMtNames.has(d.label));
  }, [data, mtById, hiddenMtNames]);

  const savedPinnedApps = useMemo(() => ctx.user.preferences?.overviewPinnedApps ?? {}, [ctx.user]);
  const savedPinnedAppIds = useMemo(
    // Rows written before pins were split per environment keep their list under the legacy key;
    // read it as the seed for whichever environment is showing, and the first toggle rewrites it
    // into the new shape.
    () => savedPinnedApps[ctx.environment] ?? ctx.user.preferences?.overviewPinnedAppIds ?? [],
    [savedPinnedApps, ctx.environment, ctx.user],
  );
  // Optimistic mirror of the persisted pins: the checkbox reacts to the click straight away and
  // only rolls back if the PATCH fails. Without this, a second click while the first save is in
  // flight was silently swallowed and the menu felt like it only accepted one app.
  const [pinnedAppIds, setPinnedAppIds] = useState<string[]>(savedPinnedAppIds);
  const pinSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const pinSavesInFlight = useRef(0);

  useEffect(() => {
    // While our own saves are running the server echo lags behind the optimistic state, so only
    // adopt it once everything settled.
    if (pinSavesInFlight.current > 0) return;
    setPinnedAppIds(savedPinnedAppIds);
  }, [savedPinnedAppIds]);

  // The legacy fallback can seed ids belonging to the other environment; they are invisible in this
  // list and must not consume slots, or the menu locks at "3/3" with nothing checked.
  const visiblePinnedAppIds = useMemo(() => {
    const present = new Set((data?.appsHealth ?? []).map((r) => r.application.id));
    return pinnedAppIds.filter((id) => present.has(id));
  }, [data, pinnedAppIds]);

  // Apps health rows reordered so pinned apps appear first (in the chosen order); the rest keep
  // their original ranking. The card shows the first rows without scrolling, so the pinned ones
  // are the "default 3".
  const orderedAppsHealth = useMemo(() => {
    const list = data?.appsHealth ?? [];
    if (pinnedAppIds.length === 0) return list;
    const rank = new Map(pinnedAppIds.map((id, i) => [id, i]));
    return [...list].sort(
      (a, b) =>
        (rank.get(a.application.id) ?? Number.POSITIVE_INFINITY) -
        (rank.get(b.application.id) ?? Number.POSITIVE_INFINITY),
    );
  }, [data, pinnedAppIds]);

  useEffect(() => {
    if (!appFilterOpen) return;
    const onDown = (e: MouseEvent) => {
      if (appFilterRef.current && !appFilterRef.current.contains(e.target as Node)) {
        setAppFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [appFilterOpen]);

  useEffect(() => {
    if (!pinMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pinMenuRef.current && !pinMenuRef.current.contains(e.target as Node)) {
        setPinMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pinMenuOpen]);

  function togglePinnedApp(appId: string) {
    const isPinned = pinnedAppIds.includes(appId);
    if (!isPinned && visiblePinnedAppIds.length >= OVERVIEW_PINNED_APPS_MAX) return;
    // Pinning drops ids the legacy fallback may have seeded from the other environment.
    const next = isPinned
      ? pinnedAppIds.filter((id) => id !== appId)
      : [...visiblePinnedAppIds, appId];
    const previous = pinnedAppIds;
    setPinnedAppIds(next);
    pinSavesInFlight.current += 1;
    // The API merges preferences shallowly, so send both environments, not just the current one.
    const patch = { ...savedPinnedApps, [ctx.environment]: next };
    // Saves run one at a time so rapid clicks can't land out of order.
    pinSaveQueue.current = pinSaveQueue.current.then(async () => {
      try {
        const { user } = await api.updatePreferences({ overviewPinnedApps: patch });
        ctx.setUser(user);
      } catch (err) {
        setPinnedAppIds(previous);
        toast.push(err instanceof Error ? err.message : 'Failed to save preferences');
      } finally {
        pinSavesInFlight.current -= 1;
      }
    });
  }

  const logsModalMt = logsModalMtId ? (mtById.get(logsModalMtId) ?? null) : null;
  const rangeLabel = ctx.range === 'mtd' ? 'mes en curso' : `rango ${ctx.range}`;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {greeting()} — <span className="serif-i">observando</span> {activeApps} aplicaciones{' '}
            <span style={{ fontSize: 14, color: 'var(--fg-3)', marginLeft: 6 }}>
              · {ctx.environment === 'development' ? 'Development' : 'Production'}
            </span>
          </h1>
          <div className="page-sub">
            Resumen del {ctx.range === 'mtd' ? 'mes en curso' : `rango ${ctx.range}`} en proyectos
            de {ctx.environment === 'development' ? 'development' : 'production'}. Las alertas
            disparadas aparecen como marcadores sobre el gráfico de logs.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="range-picker">
            {(['1h', '6h', '24h', '7d', '30d', 'mtd'] as const satisfies readonly Range[]).map(
              (r) => (
                <button
                  type="button"
                  key={r}
                  className={`range-opt ${ctx.range === r ? 'active' : ''}`}
                  onClick={() => ctx.setRange(r)}
                >
                  {r === 'mtd' ? 'MTD' : r}
                </button>
              ),
            )}
          </div>
          <div ref={appFilterRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className={appFilterIds.length > 0 ? 'btn btn-active' : 'btn'}
              onClick={() => setAppFilterOpen((v) => !v)}
            >
              <Icon.Filter size={13} />{' '}
              {appFilterIds.length > 0 ? `${appFilterIds.length} apps` : 'Filtros'}
            </button>
            {appFilterOpen && (
              <div className="pin-menu">
                <div className="pin-menu-head">Filtrar por aplicación</div>
                <div className="pin-menu-list">
                  {apps.map((a) => {
                    const checked = appFilterIds.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        className="pin-menu-item"
                        onClick={() =>
                          setAppFilterIds((prev) =>
                            prev.includes(a.id)
                              ? prev.filter((id) => id !== a.id)
                              : [...prev, a.id],
                          )
                        }
                      >
                        <span className={`pin-check ${checked ? 'on' : ''}`}>
                          {checked && <Icon.Check size={11} />}
                        </span>
                        <span className="pin-menu-name">{a.name}</span>
                        <span className="pin-menu-meta dim">
                          {a.product} / {a.company}
                        </span>
                      </button>
                    );
                  })}
                  {apps.length === 0 && <div className="feed-empty">No applications</div>}
                </div>
                {appFilterIds.length > 0 && (
                  <button
                    type="button"
                    className="pin-menu-clear"
                    onClick={() => setAppFilterIds([])}
                  >
                    Quitar filtro
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!data ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <>
          <div className="kpi-row kpi-row-5">
            <Kpi
              label="Applications"
              value={activeApps}
              unit={`/ ${apps.length}`}
              icon={Icon.Apps}
              delta={pausedApps > 0 ? `${pausedApps} en pausa` : '—'}
            />
            <Kpi
              label="Total logs (24h)"
              value={data.totals.logs.toLocaleString()}
              icon={Icon.Activity}
              spark={<SparkLine data={sparkBuckets} color="#D4B15C" />}
            />
            <Kpi
              label="Error rate"
              value={(data.totals.errorRate * 100).toFixed(2)}
              unit="%"
              icon={Icon.Alert}
              delta={errorRateDelta}
            />
            <Kpi
              label="Uptime"
              value={data.totals.uptime === null ? '—' : (data.totals.uptime * 100).toFixed(2)}
              unit={data.totals.uptime === null ? undefined : '%'}
              icon={Icon.Server}
              delta={
                data.totals.uptime === null
                  ? 'sin datos'
                  : data.totals.downSeconds > 0
                    ? `${formatDuration(data.totals.downSeconds)} sin señal`
                    : 'sin caídas'
              }
            />
            <Kpi
              label="Alerts triggered"
              value={data.totals.alertsTriggered}
              icon={Icon.Alerts}
              delta={criticalAlertsCount > 0 ? `${criticalAlertsCount} críticas` : '—'}
            />
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-h">
              <div>
                <div className="card-t">
                  Volumen de logs · {ctx.range === 'mtd' ? 'mes en curso' : `rango ${ctx.range}`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4 }}>
                  Apilado por message type · marcadores rosa indican alertas (ALERT)
                </div>
              </div>
            </div>
            <div className="card-body">
              {data.timeline.length === 0 ? (
                <div className="feed-empty">No data in this range</div>
              ) : (
                <StackedTimeline
                  timeline={data.timeline}
                  messageTypes={mts}
                  alertEvents={data.recentAlerts}
                  from={range.from}
                  to={chartTo}
                  onHiddenTypesChange={setHiddenMtNames}
                  onBucketSelect={setBucketSel}
                  onAlertSelect={(alert) => {
                    if (!alert.triggeringLogId) return;
                    ctx.navigate({
                      name: 'appdash',
                      applicationId: alert.applicationId,
                      logId: alert.triggeringLogId,
                    });
                  }}
                />
              )}
            </div>
          </div>

          <div className="grid-overview-bottom">
            <div className="card">
              <div className="card-h">
                <div className="card-t">App health</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="label-xs">{ctx.range === 'mtd' ? 'MTD' : ctx.range}</span>
                  <div ref={pinMenuRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Choose which apps to show first"
                      aria-label="Choose pinned apps"
                      onClick={() => setPinMenuOpen((v) => !v)}
                    >
                      <Icon.Filter size={13} />
                    </button>
                    {pinMenuOpen && (
                      <div className="pin-menu">
                        <div className="pin-menu-head">
                          Fijar arriba ({visiblePinnedAppIds.length}/{OVERVIEW_PINNED_APPS_MAX})
                        </div>
                        <div className="pin-menu-list">
                          {data.appsHealth.map(({ application }) => {
                            const checked = pinnedAppIds.includes(application.id);
                            const atMax =
                              !checked && visiblePinnedAppIds.length >= OVERVIEW_PINNED_APPS_MAX;
                            return (
                              <button
                                key={application.id}
                                type="button"
                                className="pin-menu-item"
                                disabled={atMax}
                                onClick={() => togglePinnedApp(application.id)}
                                title={
                                  atMax
                                    ? `Máximo ${OVERVIEW_PINNED_APPS_MAX} apps fijadas`
                                    : undefined
                                }
                              >
                                <span className={`pin-check ${checked ? 'on' : ''}`}>
                                  {checked && <Icon.Check size={11} />}
                                </span>
                                <span className="pin-menu-name">{application.name}</span>
                                <span className="pin-menu-meta dim">
                                  {application.product} / {application.company}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="app-health-scroll">
                {orderedAppsHealth.length === 0 ? (
                  <div className="feed-empty">No applications</div>
                ) : (
                  orderedAppsHealth.map(
                    ({ application, success, warning, error, neutral, healthRate }) => {
                      const a = appById.get(application.id);
                      const ratePct = healthRate * 100;
                      const rated = success + warning + error;
                      const rateColor =
                        rated === 0
                          ? 'var(--fg-4)'
                          : ratePct >= 99
                            ? 'var(--ok)'
                            : ratePct >= 95
                              ? 'var(--warn)'
                              : 'var(--err)';
                      const pct = (n: number) => (rated > 0 ? (n / rated) * 100 : 0);
                      const navTo = (mtIds?: string[]) =>
                        ctx.navigate({
                          name: 'appdash',
                          applicationId: application.id,
                          prefill:
                            mtIds && mtIds.length > 0 ? { messageTypeIds: mtIds } : undefined,
                        });
                      return (
                        // The row cannot be a button, nor carry a button role, because
                        // it nests the per-severity filter buttons below; the keyboard
                        // path into the same navigation is the app-name button.
                        // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only convenience, keyboard path is the app-name button below.
                        <div
                          key={application.id}
                          onClick={() => navTo()}
                          className="row-app-health row-hover"
                        >
                          <div className="rah-head">
                            <div className="rah-name">
                              {a ? <AppHealthDot app={a} /> : <span className="dot health-idle" />}
                              <button
                                type="button"
                                className="rah-name-btn"
                                onClick={() => navTo()}
                              >
                                {application.name}
                              </button>
                              <span className="dim">
                                · {application.product} / {application.company}
                                {application.instance ? ` / ${application.instance}` : ''}
                              </span>
                            </div>
                            <div className="rah-rate mono" style={{ color: rateColor }}>
                              {rated > 0 ? `${ratePct.toFixed(2)}%` : '—'}
                            </div>
                          </div>
                          <div className="rah-bar" aria-hidden="true">
                            <span className="seg-ok" style={{ width: `${pct(success)}%` }} />
                            <span className="seg-warn" style={{ width: `${pct(warning)}%` }} />
                            <span className="seg-err" style={{ width: `${pct(error)}%` }} />
                          </div>
                          <div className="rah-counts">
                            <button
                              type="button"
                              className="rah-count"
                              onClick={(e) => {
                                e.stopPropagation();
                                navTo(healthMtIds.success);
                              }}
                              title="Filter logs by success"
                            >
                              <span className="rah-dot" style={{ background: 'var(--ok)' }} />
                              <span className="mono">{success}</span> success
                            </button>
                            <button
                              type="button"
                              className="rah-count"
                              onClick={(e) => {
                                e.stopPropagation();
                                navTo(healthMtIds.warning);
                              }}
                              title="Filter logs by warning"
                            >
                              <span className="rah-dot" style={{ background: 'var(--warn)' }} />
                              <span className="mono">{warning}</span> warning
                            </button>
                            <button
                              type="button"
                              className="rah-count"
                              onClick={(e) => {
                                e.stopPropagation();
                                navTo(healthMtIds.error);
                              }}
                              title="Filter logs by error"
                            >
                              <span className="rah-dot" style={{ background: 'var(--err)' }} />
                              <span className="mono">{error}</span> error
                            </button>
                            <button
                              type="button"
                              className="rah-count rah-count-neutral"
                              onClick={(e) => {
                                e.stopPropagation();
                                navTo(healthMtIds.neutral);
                              }}
                              title="Filter logs by INFO / DEBUG / TRACE (neutral, not counted in health rate)"
                            >
                              <span className="rah-dot" style={{ background: 'var(--fg-4)' }} />
                              <span className="mono">{neutral}</span> sin estado
                            </button>
                          </div>
                        </div>
                      );
                    },
                  )
                )}
              </div>
            </div>

            <div className="card card-dist">
              <div className="card-h">
                <div className="card-t">
                  <span className="serif-i" style={{ fontSize: 16 }}>
                    Distribución
                  </span>{' '}
                  por message type
                </div>
              </div>
              <div
                className="card-body"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: 18,
                  alignItems: 'center',
                  alignContent: 'center',
                }}
              >
                {distributionData.length === 0 ? (
                  <div className="feed-empty" style={{ gridColumn: '1 / -1' }}>
                    No logs in this range
                  </div>
                ) : (
                  <>
                    <DonutChart
                      data={distributionData}
                      onSegmentClick={(i) => {
                        const d = distributionData[i];
                        if (d) setLogsModalMtId(d.messageTypeId);
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {distributionData.map((d) => (
                        <button
                          key={d.label}
                          type="button"
                          className="dist-legend-row"
                          onClick={() => setLogsModalMtId(d.messageTypeId)}
                          title={`Ver logs de ${d.label} en este rango`}
                        >
                          <span
                            style={{ width: 8, height: 8, borderRadius: 2, background: d.color }}
                          />
                          <span className="mono" style={{ letterSpacing: '0.04em', fontSize: 11 }}>
                            {d.label}
                          </span>
                          <span
                            className="mono"
                            style={{ marginLeft: 'auto', color: 'var(--fg-3)' }}
                          >
                            {d.value.toLocaleString()}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid-overview-bottom-2">
            <div className="card">
              <div className="card-h">
                <div className="card-t">Recent critical events</div>
                <span className="label-xs">últimos ERROR / FATAL</span>
              </div>
              <div style={{ overflow: 'hidden' }}>
                {data.recentCriticalEvents.length === 0 ? (
                  <div className="feed-empty">None in this range</div>
                ) : (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Time</th>
                        <th style={{ width: 90 }}>Type</th>
                        <th>Message</th>
                        <th style={{ width: 140 }}>Application</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentCriticalEvents.map((log) => {
                        const mt = mtById.get(log.messageTypeId);
                        const app = appById.get(log.applicationId);
                        return (
                          <tr
                            key={log.id}
                            tabIndex={0}
                            onClick={() =>
                              ctx.navigate({
                                name: 'appdash',
                                applicationId: log.applicationId,
                                logId: log.id,
                              })
                            }
                            onKeyDown={(e) =>
                              activateOnKey(e, () =>
                                ctx.navigate({
                                  name: 'appdash',
                                  applicationId: log.applicationId,
                                  logId: log.id,
                                }),
                              )
                            }
                          >
                            <td className="mono muted" style={{ fontSize: 11 }}>
                              {formatTime(log.timestamp)}
                            </td>
                            <td>
                              {mt && (
                                <span
                                  className="chip chip-mt"
                                  style={{
                                    background: `${mt.color}1F`,
                                    borderColor: `${mt.color}55`,
                                    color: mt.color,
                                  }}
                                >
                                  {mt.name}
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                maxWidth: 320,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {log.message}
                            </td>
                            <td className="muted" style={{ fontSize: 11.5 }}>
                              {app?.name ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <div className="card-t">Recent alerts</div>
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ fontSize: 11, color: 'var(--fg-3)' }}
                  onClick={() => ctx.navigate({ name: 'alerts' })}
                >
                  Ver todas <Icon.ChevronRight size={12} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {data.recentAlerts.length === 0 ? (
                  <div className="feed-empty">None in this range</div>
                ) : (
                  data.recentAlerts.slice(0, 5).map((ev) => {
                    const rule = ruleById.get(ev.ruleId);
                    const app = appById.get(ev.applicationId);
                    return (
                      <button
                        type="button"
                        key={ev.id}
                        className="plain-row-btn"
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid var(--border-1)',
                          cursor: 'pointer',
                        }}
                        onClick={() => ctx.navigate({ name: 'alerts' })}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            marginBottom: 4,
                          }}
                        >
                          {rule && (
                            <span className={`severity-chip sev-${rule.severity}`}>
                              {rule.severity}
                            </span>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{rule?.name ?? '—'}</span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 11,
                            color: 'var(--fg-3)',
                          }}
                        >
                          <span>{app?.name ?? '—'}</span>
                          <span>·</span>
                          <span>{timeAgo(ev.triggeredAt)}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {logsModalMtId && (
        <LogsModal
          ctx={ctx}
          contextLabel={`Logs · ${rangeLabel}`}
          title={
            <>
              <MessageTypeChip mt={logsModalMt} />
              <span className="serif-i" style={{ fontSize: 18, color: 'var(--fg-3)' }}>
                distribución
              </span>
            </>
          }
          from={range.from}
          to={range.to}
          initialTypeIds={[logsModalMtId]}
          applicationIds={appFilterIds.length > 0 ? appFilterIds : undefined}
          appById={appById}
          onClose={() => setLogsModalMtId(null)}
        />
      )}

      {bucketSel && (
        <LogsModal
          ctx={ctx}
          contextLabel={`Logs · ${formatBucketRange(bucketSel.fromMs, bucketSel.size)}`}
          title={
            <>
              {bucketSel.messageTypeId && (
                <MessageTypeChip mt={mtById.get(bucketSel.messageTypeId) ?? null} />
              )}
              <span className="serif-i" style={{ fontSize: 18, color: 'var(--fg-3)' }}>
                {formatBucketRange(bucketSel.fromMs, bucketSel.size)}
              </span>
            </>
          }
          from={new Date(bucketSel.fromMs).toISOString()}
          to={bucketEndIso(bucketSel.fromMs, bucketSel.size)}
          initialTypeIds={
            bucketSel.messageTypeId ? [bucketSel.messageTypeId] : bucketSel.messageTypeIds
          }
          typeChipLabel={
            bucketSel.messageTypeId
              ? (mtById.get(bucketSel.messageTypeId)?.name ?? 'tipo seleccionado')
              : 'solo tipos visibles'
          }
          applicationIds={appFilterIds.length > 0 ? appFilterIds : undefined}
          appById={appById}
          onClose={() => setBucketSel(null)}
        />
      )}
    </div>
  );
}

// Shared logs modal: used by the distribution donut (filtered by one message type)
// and by the timeline bars (filtered by a time bucket). `typeChipLabel` turns the
// type filter into a removable chip so the user can widen a bucket click to every
// message type, including the ones hidden from the chart.
function LogsModal({
  ctx,
  contextLabel,
  title,
  from,
  to,
  initialTypeIds,
  typeChipLabel,
  applicationIds,
  appById,
  onClose,
}: {
  ctx: ShellCtx;
  contextLabel: string;
  title: ReactNode;
  from: string;
  to: string;
  initialTypeIds: string[] | null;
  typeChipLabel?: string;
  applicationIds?: string[];
  appById: Map<string, ApplicationWithStats>;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [items, setItems] = useState<LogWithRelations[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeIds, setTypeIds] = useState<string[] | null>(initialTypeIds);

  const initialKey = initialTypeIds?.join(',') ?? '';
  // A new selection (another bucket, another donut slice) resets the type filter.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialTypeIds is tracked via initialKey
  useEffect(() => {
    setTypeIds(initialTypeIds);
  }, [initialKey, from, to]);

  const typeKey = typeIds?.join(',') ?? '';
  const appScopeKey = applicationIds?.join(',') ?? '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: applicationIds is tracked via appScopeKey
  useEffect(() => {
    let cancelled = false;
    // Keep previous items mounted while refetching so auto-refresh doesn't
    // collapse the table into a "Loading…" placeholder and flicker the modal.
    setError(null);
    setLoading(true);
    api
      .listLogs({
        messageTypeIds: typeIds && typeIds.length > 0 ? typeIds : undefined,
        applicationIds,
        environment: ctx.environment,
        from,
        to,
        limit: 200,
      })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setNextCursor(r.nextCursor);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [typeKey, typeIds, ctx.environment, from, to, appScopeKey]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const r = await api.listLogs({
        messageTypeIds: typeIds && typeIds.length > 0 ? typeIds : undefined,
        applicationIds,
        environment: ctx.environment,
        from,
        to,
        cursor: nextCursor,
        limit: 200,
      });
      setItems((prev) => [...(prev ?? []), ...r.items]);
      setNextCursor(r.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoading(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal xl">
        <div className="modal-head">
          <div className="modal-title-block">
            <div className="label-sm" style={{ marginBottom: 6 }}>
              {contextLabel} · {ctx.environment === 'development' ? 'Development' : 'Production'}
            </div>
            <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {title}
            </h2>
          </div>
          <button type="button" className="btn-icon btn-ghost" onClick={onClose} title="Cerrar">
            <Icon.X size={15} />
          </button>
        </div>
        {typeChipLabel && (
          <div className="modal-subbar">
            {typeIds === null ? (
              <span className="dim" style={{ fontSize: 11.5 }}>
                Mostrando todos los message types
              </span>
            ) : (
              <button
                type="button"
                className="filter-chip"
                onClick={() => setTypeIds(null)}
                title="Quitar el filtro y ver todos los message types"
              >
                {typeChipLabel} <Icon.X size={10} />
              </button>
            )}
          </div>
        )}
        <div className="modal-body" style={{ padding: 0 }}>
          {error && (
            <div className="error-banner" style={{ margin: 16 }}>
              {error}
            </div>
          )}
          {items === null ? (
            <div className="feed-empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="feed-empty">No logs in this range</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Time</th>
                  <th style={{ width: 160 }}>Application</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {items.map((log) => {
                  const a = appById.get(log.applicationId);
                  const openLog = () => {
                    onClose();
                    ctx.navigate({
                      name: 'appdash',
                      applicationId: log.applicationId,
                      logId: log.id,
                    });
                  };
                  return (
                    <tr
                      key={log.id}
                      tabIndex={0}
                      onClick={openLog}
                      onKeyDown={(e) => activateOnKey(e, openLog)}
                    >
                      <td className="mono muted" style={{ fontSize: 11 }}>
                        {formatTime(log.timestamp)}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {a && <AppHealthDot app={a} />}
                          <span>{log.application.name}</span>
                        </span>
                      </td>
                      <td
                        style={{
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.message}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-foot">
          <div className="modal-foot-info">
            {items ? `${items.length.toLocaleString()} logs cargados` : ''}
            {nextCursor ? ' · hay más' : ''}
          </div>
          <div className="modal-foot-actions">
            {nextCursor && (
              <button type="button" className="btn btn-ghost" onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
